import React, { useState } from "react";
import {
  Table,
  Pagination,
  Select,
  Skeleton,
  Empty,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  Upload,
  Popconfirm,
  Input as AntInput,
} from "antd";
import Column from "antd/es/table/Column";
import { UploadOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import useAxiosInstance from "../../lib/useAxiosInstance";

const StringerList = () => {
  const axiosInstance = useAxiosInstance();

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [keyword, setKeyword] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Bulk upload state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkUploading, setBulkUploading] = useState(false);

  const fetchStringers = async () => {
    const response = await axiosInstance.get(
      `api/news/stringers?page=${currentPage}&limit=${limit}&keyword=${encodeURIComponent(
        keyword
      )}`
    );
    return response?.data?.data || {};
  };

  const stringersQuery = useQuery(
    ["stringers-list", currentPage, limit, keyword],
    fetchStringers,
    { refetchOnWindowFocus: false, retry: 1 }
  );

  const isLoading = stringersQuery?.isFetching || stringersQuery?.isLoading;
  const rows = stringersQuery?.data?.data || [];
  const totalPages = stringersQuery?.data?.totalPages || 0;

  const refetch = () => stringersQuery.refetch();

  // ----- Add / Edit -----
  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ active: true });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      district: record.district,
      phone: record.phone,
      active: record.active,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (editing) {
        await axiosInstance.put(`api/news/stringers/${editing._id}`, values);
        toast.success("Stringer updated successfully");
      } else {
        await axiosInstance.post(`api/news/stringers`, values);
        toast.success("Stringer added successfully");
      }
      setModalOpen(false);
      refetch();
    } catch (err) {
      if (err?.errorFields) return; // validation error
      toast.error(
        err?.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await axiosInstance.delete(`api/news/stringers/${record._id}`);
      toast.success("Stringer deleted successfully");
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete stringer");
    }
  };

  // ----- Bulk upload -----
  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        setBulkRows(json);
        setBulkFileName(file.name);
      } catch (err) {
        toast.error("Failed to parse the file. Please upload a valid Excel sheet.");
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // prevent auto upload
  };

  const handleBulkUpload = async () => {
    if (!bulkRows.length) {
      toast.error("Please select a file with stringer data first.");
      return;
    }
    try {
      setBulkUploading(true);
      const response = await axiosInstance.post(`api/news/stringers/bulk`, {
        stringers: bulkRows,
      });
      const result = response?.data?.data?.data || {};
      toast.success(
        response?.data?.message ||
          `Inserted ${result.inserted || 0}, skipped ${result.skipped || 0}`
      );
      setBulkOpen(false);
      setBulkRows([]);
      setBulkFileName("");
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Bulk upload failed");
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <>
      <div className='flex items-center justify-between mb-4 px-2'>
        <AntInput.Search
          placeholder='Search by name, district or phone'
          allowClear
          style={{ width: 320 }}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={(val) => {
            setKeyword(val);
            setCurrentPage(1);
          }}
        />
        <Space>
          <Button onClick={() => setBulkOpen(true)}>Bulk Upload</Button>
          <Button type='primary' onClick={openAdd}>
            Add Stringer
          </Button>
        </Space>
      </div>

      <div className='reports-table'>
        <Table
          rowKey={(record) => record._id}
          dataSource={rows}
          pagination={false}
          locale={{
            emptyText: isLoading ? (
              <div style={{ marginLeft: "20px", width: "95%" }}>
                <Skeleton
                  title={false}
                  active
                  paragraph={{ rows: 6 }}
                />
              </div>
            ) : (
              <Empty />
            ),
          }}
        >
          <Column
            title='Name'
            dataIndex='name'
            render={(t) => <span>{t || "-"}</span>}
          />
          <Column
            title='District'
            dataIndex='district'
            render={(t) => <span>{t || "-"}</span>}
          />
          <Column
            title='Phone'
            dataIndex='phone'
            render={(t) => <span>{t || "-"}</span>}
          />
          <Column
            title='Status'
            dataIndex='active'
            render={(active) => (
              <span style={{ color: active ? "green" : "red" }}>
                {active ? "Active" : "Inactive"}
              </span>
            )}
          />
          <Column
            title='Created At'
            dataIndex='createdAt'
            render={(t) => <span>{t ? new Date(t).toLocaleString() : "-"}</span>}
          />
          <Column
            title='Actions'
            render={(_, record) => (
              <Space>
                <Button size='small' onClick={() => openEdit(record)}>
                  Edit
                </Button>
                <Popconfirm
                  title='Delete this stringer?'
                  okText='Yes'
                  cancelText='No'
                  onConfirm={() => handleDelete(record)}
                >
                  <Button size='small' danger>
                    Delete
                  </Button>
                </Popconfirm>
              </Space>
            )}
          />
        </Table>

        <div className='report-pagination'>
          <div>
            <Select
              value={limit.toString()}
              style={{ width: 56 }}
              onChange={(value) => {
                setLimit(parseInt(value));
                setCurrentPage(1);
              }}
              options={[
                { value: "10", label: "10" },
                { value: "20", label: "20" },
                { value: "50", label: "50" },
                { value: "100", label: "100" },
              ]}
            />
            <span className='px-4'>Entries Per Page</span>
          </div>
          <div className='report-selection'>
            <Pagination
              className='pagination'
              current={currentPage}
              pageSize={limit}
              total={totalPages * limit}
              onChange={(page, pageSize) => {
                setCurrentPage(page);
                setLimit(pageSize);
              }}
            />
          </div>
        </div>
      </div>

      {/* Add / Edit modal */}
      <Modal
        title={editing ? "Edit Stringer" : "Add Stringer"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText={editing ? "Update" : "Add"}
        destroyOnClose
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='name'
            label='Name'
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input placeholder='Stringer name' />
          </Form.Item>
          <Form.Item
            name='district'
            label='District'
            rules={[{ required: true, message: "Please enter district" }]}
          >
            <Input placeholder='District' />
          </Form.Item>
          <Form.Item
            name='phone'
            label='Phone (WhatsApp)'
            rules={[
              { required: true, message: "Please enter phone number" },
              {
                pattern: /^[0-9]{10,15}$/,
                message: "Enter a valid phone number (10-15 digits, country code)",
              },
            ]}
          >
            <Input placeholder='e.g. 919876543210' />
          </Form.Item>
          <Form.Item name='active' label='Active' valuePropName='checked'>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk upload modal */}
      <Modal
        title='Bulk Upload Stringers'
        open={bulkOpen}
        onCancel={() => {
          setBulkOpen(false);
          setBulkRows([]);
          setBulkFileName("");
        }}
        onOk={handleBulkUpload}
        confirmLoading={bulkUploading}
        okText='Upload'
        destroyOnClose
      >
        <p style={{ marginBottom: 12 }}>
          Upload an Excel (.xlsx / .csv) file with columns:{" "}
          <strong>name</strong>, <strong>district</strong>,{" "}
          <strong>phone</strong>.
        </p>
        <Upload
          accept='.xlsx,.xls,.csv'
          beforeUpload={handleFile}
          maxCount={1}
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />}>Select File</Button>
        </Upload>
        {bulkFileName && (
          <div style={{ marginTop: 12 }}>
            <strong>{bulkFileName}</strong> — {bulkRows.length} row(s) detected
          </div>
        )}
      </Modal>
    </>
  );
};

export default StringerList;
